import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import {
  Alert,
  Button,
  Checkbox,
  Dropdown,
  Empty,
  Input,
  Modal,
  Pagination,
  Select,
  Space,
  Spin,
  Tooltip,
  message,
} from 'antd'
import {
  AppstoreOutlined,
  ArrowLeftOutlined,
  CloudUploadOutlined,
  DeleteOutlined,
  DownloadOutlined,
  EditOutlined,
  FolderOpenOutlined,
  FolderOutlined,
  MobileOutlined,
  MoreOutlined,
  PlusOutlined,
  ReloadOutlined,
  SearchOutlined,
  UnorderedListOutlined,
} from '@ant-design/icons'
import {
  createImageCollection,
  deleteImageCollection,
  getImageLibraryItem,
  getUploadConfig,
  listImageCollections,
  listImageLibrary,
  moveImagesToCollection,
  renameImageCollection,
  setImageVisibility,
  setPipelineApiScope,
  startChainProcessing,
  triggerImageAssetDownload,
  triggerImageAssetDownloadsNative,
  type ImageCollection,
  type ImageLibraryView,
  type ImageOrigin,
  type ImageOriginCounts,
  type ImageTeamBlock,
  type ImageVisibility,
  type LibraryAssetItem,
  type UploadConfig,
} from '../../api/client'
import {
  batchDeleteImagePickerItems,
  createContentProject,
  deleteImagePickerItem,
  resolveImagePickerSelection,
  workspaceImagesBaseUrl,
} from '../api'
import { syncCanvasNavigationContextFromProject } from '../navigation/canvasNavigationContext'
import { openCanvasFromWorkspace } from './canvasWorkspaceTransition'
import {
  makeJoinImageAssetSeedNonce,
  writeJoinImageAssetSeed,
} from './imageAssetJoinCanvasSeed'
import {
  imageAssetPermissions,
  itemsLackingManage,
} from './team/imageAssetPermissions'
import {
  buildCreatorSelectOptions,
  creatorFilterAfterViewChange,
  sanitizeCreatorFilter,
} from './team/creatorFilterOptions'
import MobileCaptureQrModal from '../components/MobileCaptureQrModal'
import ImageAssetUploadModal from './imageUpload/ImageAssetUploadModal'
import ImageAssetHiResViewer from './ImageAssetHiResViewer'
import {
  buildLibraryListParams,
  canGoNext,
  canGoPrev,
  createNavSessionFromPage,
  indexInPageForAbsolute,
  pageForAbsoluteIndex,
  snapshotNavQuery,
  type ImageAssetViewerNavSession,
} from './imageAssetViewerNavigation'
import { imageUploadManager, useWorkspaceUploadBatches } from './imageUpload/imageUploadManager'
import { createRefreshThrottle } from './imageUpload/refreshThrottle'
import { resolveUploadLimits } from './imageUpload/uploadLimits'
import { useWorkspaceState } from './WorkspaceContext'
import { useImageAssetCanvasEditor } from './ImageAssetCanvasEditorLayer'
import { VistoeraActionConfirm } from '../ui/action'
import { invalidateCachedPickerCount } from '../components/ImageAssetPicker/imagePickerCountCache'
import { applyDeleteDelta } from './imageAssetDeleteOptimisticDelta'
import {
  EMPTY_ORIGIN_COUNTS,
  ORIGIN_CARD_KEYS,
  applyOriginDeleteDelta,
  normalizeOriginCounts,
  originBadgeClass,
  originOf,
} from './imageOrigin'
import {
  classifyLoadError,
  createNotifyDeduper,
  describeFailure,
  isCancelledError,
  withReadRetry,
  type ClassifiedLoadError,
} from './imageAssetLoadErrors'
import ImageFolderBreadcrumb from '../components/ImageFolderTree/ImageFolderBreadcrumb'
import ImageFolderTree from '../components/ImageFolderTree/ImageFolderTree'
import FolderNameModal, { type FolderNameModalState } from '../components/ImageFolderTree/FolderNameModal'
import { folderPath } from '../components/ImageFolderTree/folderTreeModel'

const FOLDER_TREE_OPEN_KEY = 'vistoera.imageAssets.folderTreeOpen'
import './image-asset-center.css'

/** One shared de-duplicator for this page's failure toasts: the same failure
 * kind is shown at most once per window no matter how many requests / effects
 * hit it (VistoeraTASK-00522). */
const loadErrorNotifier = createNotifyDeduper(10_000)

const PAGE_SIZE_OPTIONS = [20, 50, 100]
const VIEW_MODE_KEY = 'image_asset_view_mode'

/** Existing formal editor routes (display names: 物品图像处理 / 平面图像处理). */
export const ITEM_STANDARD_EDITOR_ROUTE = (workspaceId: string) =>
  `/workspace/${workspaceId}/images/process`
export const FLAT_IMAGE_EDITOR_ROUTE = (workspaceId: string, chainId?: string | null) =>
  chainId
    ? `/workspace/${workspaceId}/images/flat-optimization/${chainId}`
    : `/workspace/${workspaceId}/images/flat-optimization`

const KNOWN_TYPE_KEYS = [
  'all',
  'original',
  'transparent',
  'color',
  'shadow',
  'flat_rectified',
  'flat_enhanced',
  'ai_generated',
  'crop',
  'other',
] as const

function formatBytes(n: number | null | undefined): string {
  if (n == null || Number.isNaN(n)) return '—'
  if (n < 1024) return `${n} B`
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`
  return `${(n / (1024 * 1024)).toFixed(1)} MB`
}

function formatDims(w: number | null, h: number | null): string {
  if (!w || !h) return '—'
  return `${w} × ${h}`
}

/** Absolute local time: YYYY-MM-DD HH:mm (no relative, no seconds). */
function formatAbsoluteTime(iso: string | null | undefined): string {
  if (!iso) return '—'
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return '—'
  const y = d.getFullYear()
  const mo = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  const hh = String(d.getHours()).padStart(2, '0')
  const mm = String(d.getMinutes()).padStart(2, '0')
  return `${y}-${mo}-${day} ${hh}:${mm}`
}

function pickerItemId(item: LibraryAssetItem): string {
  if (item.picker_item_id) return item.picker_item_id
  if (item.id.startsWith('sa:') || item.id.startsWith('lib:')) return item.id
  return `lib:${item.id}`
}

function siteAssetIdFromItem(item: LibraryAssetItem): string | null {
  const pid = pickerItemId(item)
  if (pid.startsWith('sa:')) return pid.slice(3)
  if (item.reference_type === 'site_asset' && item.id && !item.id.includes(':')) return item.id
  return null
}

/** Initial of a REAL member display name for the creator avatar (no avatar
 * image field exists on the user profile; the initial is derived from the
 * real name, never fabricated). */
function creatorInitial(name: string | null | undefined): string {
  const trimmed = (name || '').trim()
  return trimmed ? Array.from(trimmed)[0].toUpperCase() : '?'
}

function avatarColor(seed: string | null | undefined): string | undefined {
  if (!seed) return undefined
  let h = 0
  for (let i = 0; i < seed.length; i += 1) h = (h * 31 + seed.charCodeAt(i)) % 360
  return `hsl(${h} 45% 42%)`
}

function readStoredViewMode(): 'grid' | 'list' {
  try {
    const v = localStorage.getItem(VIEW_MODE_KEY)
    if (v === 'list' || v === 'grid') return v
  } catch {
    /* ignore */
  }
  return 'grid'
}

export default function ImageAssetCenterPage() {
  const navigate = useNavigate()
  const { t } = useTranslation('imageAssets')
  const { t: tActions } = useTranslation('actions')
  const { t: tNav } = useTranslation('navigation')
  const { t: tProjects } = useTranslation('contentProjects')
  const { workspace, entitlements, primarySite } = useWorkspaceState()
  const itemStandardLabel = tNav('tenantNav.images.itemStandard')
  const flatOptimizationLabel = tNav('tenantNav.images.flatOptimization')
  const asideNavLabels = useMemo(
    () => ({
      itemStandard: itemStandardLabel,
      flatOptimization: flatOptimizationLabel,
    }),
    [itemStandardLabel, flatOptimizationLabel],
  )

  const typeLabel = useCallback(
    (item: LibraryAssetItem): string => {
      if (item.resource_type_label) return item.resource_type_label
      if (item.asset_type_label) return item.asset_type_label
      const assetType = item.asset_type
      if (assetType && (KNOWN_TYPE_KEYS as readonly string[]).includes(assetType)) {
        return t(`types.${assetType}.label`)
      }
      return item.asset_type || t('types.other.label')
    },
    [t],
  )
  const workspaceId = workspace?.id || ''
  const imageProcessingEnabled = Boolean(entitlements['feature.image_processing'])

  const [scopeReady, setScopeReady] = useState(false)
  const [loading, setLoading] = useState(false)
  const [items, setItems] = useState<LibraryAssetItem[]>([])
  const [summary, setSummary] = useState<Record<string, number>>({})
  // VistoeraTASK-00557: the four first-level cards (all / upload / AI / edit).
  const [originCounts, setOriginCounts] = useState<ImageOriginCounts>(EMPTY_ORIGIN_COUNTS)
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(20)
  const [q, setQ] = useState('')
  const [origin, setOrigin] = useState<'all' | ImageOrigin>('all')
  const [sort, setSort] = useState<'recent' | 'oldest' | 'name'>('recent')
  const [viewMode, setViewMode] = useState<'grid' | 'list'>(readStoredViewMode)
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())

  const [uploadOpen, setUploadOpen] = useState(false)
  const [mobileCaptureOpen, setMobileCaptureOpen] = useState(false)
  const [mobileCapturePolling, setMobileCapturePolling] = useState(false)
  const [uploadConfig, setUploadConfig] = useState<UploadConfig | null>(null)
  const [previewItem, setPreviewItem] = useState<LibraryAssetItem | null>(null)
  const [previewLoading, setPreviewLoading] = useState(false)
  const previewSeqRef = useRef(0)
  /** VistoeraTASK-00685 — folder-scoped viewer nav (snapshot of list query at open). */
  const [previewNavSession, setPreviewNavSession] = useState<ImageAssetViewerNavSession | null>(null)
  const [previewNavBusy, setPreviewNavBusy] = useState(false)
  const previewNavSeqRef = useRef(0)
  const previewPagesRef = useRef<Map<number, LibraryAssetItem[]>>(new Map())
  const previewNavSessionRef = useRef<ImageAssetViewerNavSession | null>(null)
  const [batchDownloading, setBatchDownloading] = useState(false)
  const [singleDownloadingId, setSingleDownloadingId] = useState<string | null>(null)
  // VistoeraTASK-00249 — synchronous re-entrancy guard for `onBatchDelete`
  // (same "plain ref, not React state" reasoning as
  // `VistoeraActionConfirm.submittingRef`: a ref mutates immediately,
  // closing the same-tick double-invocation race a `useState` flag can't).
  const batchDeleteInFlightRef = useRef(false)

  // --- Team sharing (VistoeraTASK-00508) ---------------------------------
  // `team` comes from the SAME list response (no extra request): it is null
  // until the first load and `{enabled:false}` for personal Workspaces, in
  // which case nothing below ever renders or fetches. The server decides team
  // mode from real active memberships; this is display state only - every
  // permission is re-checked by the backend.
  const [team, setTeam] = useState<ImageTeamBlock | null>(null)
  const teamEnabled = Boolean(team?.enabled)
  const teamEnabledRef = useRef(false)
  const [view, setView] = useState<ImageLibraryView>('mine')
  const [creatorFilter, setCreatorFilter] = useState<string>('all')
  const [collectionId, setCollectionId] = useState<string | null>(null)
  const [collections, setCollections] = useState<ImageCollection[]>([])
  const [collectionsVersion, setCollectionsVersion] = useState(0)
  // TASK-00533: the SAME create/rename dialog the Canvas picker uses.
  const [folderModal, setFolderModal] = useState<FolderNameModalState | null>(null)
  const [treeOpen, setTreeOpen] = useState<boolean>(() => {
    try {
      return localStorage.getItem(FOLDER_TREE_OPEN_KEY) !== '0'
    } catch {
      return true
    }
  })
  const toggleTree = useCallback(() => {
    setTreeOpen((prev) => {
      const next = !prev
      try {
        localStorage.setItem(FOLDER_TREE_OPEN_KEY, next ? '1' : '0')
      } catch {
        /* storage unavailable - display preference only */
      }
      return next
    })
  }, [])
  const [moveTargets, setMoveTargets] = useState<LibraryAssetItem[] | null>(null)
  const [moveCollectionId, setMoveCollectionId] = useState<string | null>(null)
  const [moveSaving, setMoveSaving] = useState(false)
  const bumpCollections = useCallback(() => setCollectionsVersion((v) => v + 1), [])

  // --- Load-failure state (VistoeraTASK-00522) ------------------------------
  // A failed list / collections read keeps whatever is already on screen and
  // shows ONE inline, recoverable notice instead of a stack of raw
  // "Network Error" toasts.
  const [loadError, setLoadError] = useState<ClassifiedLoadError | null>(null)
  const [collectionsError, setCollectionsError] = useState<ClassifiedLoadError | null>(null)
  const loadSeqRef = useRef(0)
  const loadAbortRef = useRef<AbortController | null>(null)
  const collectionsSeqRef = useRef(0)
  const collectionsAbortRef = useRef<AbortController | null>(null)

  useEffect(() => {
    if (!workspaceId) return
    setPipelineApiScope(workspaceImagesBaseUrl(workspaceId))
    setScopeReady(true)
    return () => {
      setPipelineApiScope(null)
      setScopeReady(false)
    }
  }, [workspaceId])

  useEffect(() => {
    return () => {
      loadAbortRef.current?.abort()
      collectionsAbortRef.current?.abort()
    }
  }, [])

  const persistViewMode = (mode: 'grid' | 'list') => {
    setViewMode(mode)
    try {
      localStorage.setItem(VIEW_MODE_KEY, mode)
    } catch {
      /* ignore */
    }
  }

  const load = useCallback(async () => {
    if (!workspaceId || !scopeReady) return
    // A newer load supersedes (and aborts) any older one: only the latest
    // response may touch state, and an aborted request is never an error.
    const seq = ++loadSeqRef.current
    loadAbortRef.current?.abort()
    const abort = new AbortController()
    loadAbortRef.current = abort
    const startedAt = performance.now()
    let retryCount = 0
    setLoading(true)
    try {
      const params: NonNullable<Parameters<typeof listImageLibrary>[0]> = {
        q: q.trim() || undefined,
        origin: origin === 'all' ? undefined : origin,
        page,
        page_size: pageSize,
        sort,
      }
      // Team scope params are only ever sent once the server said this is a
      // team Workspace (personal requests stay byte-identical to before).
      if (teamEnabledRef.current) {
        params.view = view
        if (creatorFilter !== 'all') params.creator_id = creatorFilter
        if (collectionId) params.collection_id = collectionId
      }
      const { data } = await withReadRetry(
        (signal) => listImageLibrary(params, signal),
        {
          signal: abort.signal,
          offline: () => typeof navigator !== 'undefined' && navigator.onLine === false,
          onRetry: () => {
            retryCount += 1
          },
        },
      )
      if (seq !== loadSeqRef.current) return
      setLoadError(null)
      teamEnabledRef.current = Boolean(data.team?.enabled)
      setTeam(data.team ?? null)
      setItems(data.items)
      setTotal(data.total)
      setSummary(data.summary || {})
      setOriginCounts(normalizeOriginCounts(data.origins, data.summary?.all ?? data.total))
      setCreatorFilter((prev) => sanitizeCreatorFilter(prev, data.team?.creator_options))
      // Drop selections that are no longer on this page / universe.
      setSelectedIds((prev) => {
        if (prev.size === 0) return prev
        const keep = new Set(data.items.map((i) => i.id))
        const next = new Set([...prev].filter((id) => keep.has(id)))
        return next.size === prev.size ? prev : next
      })
    } catch (e) {
      if (seq !== loadSeqRef.current || isCancelledError(e)) return
      const failure = classifyLoadError(e, { offline: typeof navigator !== 'undefined' && navigator.onLine === false })
      setLoadError(failure)
      // Structured, secret-free diagnostics (path / status / duration / retries).
      console.warn(
        '[image-assets] library load failed',
        describeFailure({
          path: '/library',
          error: failure,
          durationMs: performance.now() - startedAt,
          retryCount,
          stage: 'library',
        }),
      )
      if (loadErrorNotifier.shouldNotify(`library:${failure.kind}`)) {
        message.error({
          key: 'iac-load-error',
          content:
            failure.kind === 'other' && failure.serverMessage
              ? failure.serverMessage
              : t(`loadError.${failure.kind}.title`),
        })
      }
    } finally {
      if (seq === loadSeqRef.current) setLoading(false)
    }
  }, [workspaceId, scopeReady, q, origin, page, pageSize, sort, view, creatorFilter, collectionId, t])

  useEffect(() => {
    void load()
  }, [load])

  const reloadAfterEditorSave = useCallback(async () => {
    invalidateCachedPickerCount(workspaceId)
    await load()
  }, [workspaceId, load])

  const { openForItem, editorLayer } = useImageAssetCanvasEditor({
    workspaceId,
    collectionId: collectionId || null,
    onSaved: reloadAfterEditorSave,
  })

  const openImageEditor = useCallback(
    (item: LibraryAssetItem) => {
      if (teamEnabled && team && !imageAssetPermissions(item, team).canOrganize) {
        message.error(t('imageEditorEntry.openFailed'))
        return
      }
      void openForItem(item, primarySite?.id)
    },
    [teamEnabled, team, openForItem, primarySite?.id, t],
  )

  /** VistoeraTASK-00625 — create a NEW blank Content Project, seed one
   * image_asset node, open it with the same Workspace→Canvas Enter animation
   * as the V logo (`openCanvasFromWorkspace`). Never mutates the previously
   * open project. */
  const joinCanvasInFlightRef = useRef(false)
  const onJoinCanvas = useCallback(
    async (item: LibraryAssetItem) => {
      if (!workspaceId) return
      if (teamEnabled && team && !imageAssetPermissions(item, team).canOrganize) {
        message.error(t('joinCanvas.openFailed'))
        return
      }
      if (!primarySite?.id) {
        message.error(t('joinCanvas.noSite'))
        return
      }
      if (joinCanvasInFlightRef.current) return
      joinCanvasInFlightRef.current = true
      const hide = message.loading({ content: t('joinCanvas.opening'), duration: 0 })
      try {
        let siteAssetId = siteAssetIdFromItem(item)
        if (!siteAssetId) {
          const { data } = await resolveImagePickerSelection(workspaceId, {
            picker_item_id: pickerItemId(item),
            target: 'site_asset',
            site_id: primarySite.id,
          })
          siteAssetId = data.asset_id
        }
        if (!siteAssetId) {
          message.error(t('joinCanvas.openFailed'))
          return
        }
        const { data: project } = await createContentProject(
          workspaceId,
          tProjects('unnamedProject'),
        )
        if (!project?.id) {
          message.error(t('joinCanvas.createFailed'))
          return
        }
        writeJoinImageAssetSeed({
          workspaceId,
          projectId: project.id,
          siteAssetId,
          pickerItemId: pickerItemId(item),
          filename: item.filename,
          nonce: makeJoinImageAssetSeedNonce(),
        })
        syncCanvasNavigationContextFromProject(workspaceId, project.id, 'content-production')
        openCanvasFromWorkspace(
          navigate,
          `/workspace/${workspaceId}/content-production/${project.id}/canvas`,
        )
      } catch (e) {
        message.error((e as Error)?.message || t('joinCanvas.openFailed'))
      } finally {
        hide()
        joinCanvasInFlightRef.current = false
      }
    },
    [workspaceId, teamEnabled, team, primarySite?.id, navigate, t, tProjects],
  )

  // Collections (team mode only): counts follow the active view so they always
  // agree with what entering a collection shows.
  const loadCollections = useCallback(async () => {
    if (!scopeReady || !teamEnabled) return
    const seq = ++collectionsSeqRef.current
    collectionsAbortRef.current?.abort()
    const abort = new AbortController()
    collectionsAbortRef.current = abort
    const startedAt = performance.now()
    let retryCount = 0
    try {
      const { data } = await withReadRetry(
        (signal) => listImageCollections(view === 'mine' ? 'all' : view, signal),
        {
          signal: abort.signal,
          offline: () => typeof navigator !== 'undefined' && navigator.onLine === false,
          onRetry: () => {
            retryCount += 1
          },
        },
      )
      if (seq !== collectionsSeqRef.current) return
      setCollections(data.collections)
      setCollectionsError(null)
    } catch (e) {
      if (seq !== collectionsSeqRef.current || isCancelledError(e)) return
      const failure = classifyLoadError(e, { offline: typeof navigator !== 'undefined' && navigator.onLine === false })
      // Keep the libraries already on screen; show a local retry affordance
      // (below) instead of a toast that reads like the whole site is down.
      setCollectionsError(failure)
      console.warn(
        '[image-assets] collections load failed',
        describeFailure({
          path: '/collections',
          error: failure,
          durationMs: performance.now() - startedAt,
          retryCount,
          stage: 'collections',
        }),
      )
    }
  }, [scopeReady, teamEnabled, view])

  useEffect(() => {
    void loadCollections()
  }, [loadCollections, collectionsVersion])

  // Come back online / the tab becomes visible again while a failure notice
  // is showing -> try once more automatically (no polling).
  // --- Background uploads (VistoeraTASK-00522) ------------------------------
  // The upload manager is global (it outlives this page and the dialog); here we
  // only (a) mirror its progress in a small header chip and (b) refresh the
  // visible list at most once per interval - plus once when a batch settles -
  // instead of once per finished file (500 uploads => a handful of reads).
  const uploadBatches = useWorkspaceUploadBatches(workspaceId)
  const loadLatestRef = useRef(load)
  loadLatestRef.current = load
  const uploadRefreshMs = useMemo(() => resolveUploadLimits(uploadConfig).listRefreshIntervalMs, [uploadConfig])
  useEffect(() => {
    if (!workspaceId) return undefined
    const throttle = createRefreshThrottle(uploadRefreshMs, () => {
      invalidateCachedPickerCount(workspaceId)
      void loadLatestRef.current()
      if (teamEnabledRef.current) bumpCollections()
    })
    const off = imageUploadManager.subscribeEvents((e) => {
      if (e.workspaceId !== workspaceId) return
      if (e.type === 'item-done') throttle.markDirty()
      else throttle.flushNow()
    })
    return () => {
      off()
      throttle.cancel()
    }
  }, [workspaceId, uploadRefreshMs, bumpCollections])

  // Mobile QR session only: refresh the asset list while the modal is open.
  // No idle polling — session start is gated on the user clicking 手机上传.
  useEffect(() => {
    if (!mobileCapturePolling || !workspaceId || !scopeReady) return undefined
    const tick = () => {
      invalidateCachedPickerCount(workspaceId)
      void loadLatestRef.current()
      if (teamEnabledRef.current) bumpCollections()
    }
    tick()
    const timer = window.setInterval(tick, Math.max(2000, uploadRefreshMs))
    return () => window.clearInterval(timer)
  }, [mobileCapturePolling, workspaceId, scopeReady, uploadRefreshMs, bumpCollections])

  const uploadChip = useMemo(() => {
    const active = uploadBatches.filter((b) => b.finishedAt === null)
    if (active.length > 0) {
      const total = active.reduce((n, b) => n + b.counts.total, 0)
      const done = active.reduce((n, b) => n + b.counts.done + b.counts.failed + b.counts.cancelled, 0)
      return { label: t('uploadBulk.chip', { done, total }), warn: false }
    }
    const failed = uploadBatches.reduce((n, b) => n + b.counts.failed, 0)
    if (failed > 0) return { label: t('uploadBulk.chipFailed', { failed }), warn: true }
    return null
  }, [uploadBatches, t])

  const hasLoadFailure = Boolean(loadError || collectionsError)
  useEffect(() => {
    if (!hasLoadFailure) return undefined
    const retry = () => {
      if (typeof navigator !== 'undefined' && navigator.onLine === false) return
      if (loadError) void load()
      if (collectionsError) void loadCollections()
    }
    const onVisible = () => {
      if (document.visibilityState === 'visible') retry()
    }
    window.addEventListener('online', retry)
    document.addEventListener('visibilitychange', onVisible)
    return () => {
      window.removeEventListener('online', retry)
      document.removeEventListener('visibilitychange', onVisible)
    }
  }, [hasLoadFailure, loadError, collectionsError, load, loadCollections])

  useEffect(() => {
    if (!scopeReady) return
    getUploadConfig()
      .then((r) => setUploadConfig(r.data))
      .catch(() => setUploadConfig(null))
  }, [scopeReady])

  const applyPreviewItem = useCallback(async (item: LibraryAssetItem) => {
    const seq = ++previewSeqRef.current
    setPreviewItem(item)
    setPreviewLoading(true)
    try {
      const detailId = pickerItemId(item)
      const { data } = await getImageLibraryItem(detailId)
      if (previewSeqRef.current !== seq) return
      // Keep list-tier preview_url for instant paint; merge detail for
      // display_url / full_url / metadata.
      setPreviewItem({
        ...item,
        ...data,
        preview_url: item.preview_url || data.preview_url,
        display_url: data.display_url || item.display_url || data.preview_url || item.preview_url,
      })
    } catch {
      /* keep list row for modal; display/full may still work */
    } finally {
      if (previewSeqRef.current === seq) setPreviewLoading(false)
    }
  }, [])

  /** Open viewer from the current list page — seeds folder-scoped nav. */
  const openPreview = async (item: LibraryAssetItem) => {
    const query = snapshotNavQuery({
      q,
      origin,
      sort,
      pageSize,
      teamEnabled,
      view,
      creatorFilter,
      collectionId,
    })
    const session = createNavSessionFromPage({
      items,
      item,
      page,
      pageSize,
      total,
      query,
    })
    previewPagesRef.current = new Map([[page, [...items]]])
    previewNavSessionRef.current = session
    setPreviewNavSession(session)
    await applyPreviewItem(item)
  }

  const closePreview = () => {
    previewSeqRef.current += 1
    previewNavSeqRef.current += 1
    previewNavSessionRef.current = null
    setPreviewItem(null)
    setPreviewLoading(false)
    setPreviewNavSession(null)
    setPreviewNavBusy(false)
    previewPagesRef.current = new Map()
  }

  const ensurePreviewPageItems = useCallback(
    async (
      targetPage: number,
      session: ImageAssetViewerNavSession,
      navSeq: number,
    ): Promise<LibraryAssetItem[] | null> => {
      const cached = previewPagesRef.current.get(targetPage)
      if (cached) return cached
      const { data } = await listImageLibrary(buildLibraryListParams(session.query, targetPage))
      if (navSeq !== previewNavSeqRef.current) return null
      previewPagesRef.current.set(targetPage, data.items)
      setPreviewNavSession((prev) =>
        prev
          ? {
              ...prev,
              total: typeof data.total === 'number' ? data.total : prev.total,
            }
          : prev,
      )
      return data.items
    },
    [],
  )

  /** VistoeraTASK-00685 — prev/next within the snapshotted list query (cross-page).
   * Rapid clicks bump `previewNavSeqRef` so only the latest target wins. */
  const navigatePreviewBy = useCallback(
    async (delta: -1 | 1) => {
      const session = previewNavSessionRef.current
      if (!session) return
      if (delta < 0 && !canGoPrev(session.absoluteIndex)) return
      if (delta > 0 && !canGoNext(session.absoluteIndex, session.total)) return
      const nextAbs = session.absoluteIndex + delta
      const nextSession: ImageAssetViewerNavSession = { ...session, absoluteIndex: nextAbs }
      previewNavSessionRef.current = nextSession
      setPreviewNavSession(nextSession)
      setPreviewNavBusy(true)
      const navSeq = ++previewNavSeqRef.current
      try {
        const pageSizeNav = session.query.pageSize
        const targetPage = pageForAbsoluteIndex(nextAbs, pageSizeNav)
        const idxInPage = indexInPageForAbsolute(nextAbs, pageSizeNav)
        const pageItems = await ensurePreviewPageItems(targetPage, session, navSeq)
        if (navSeq !== previewNavSeqRef.current || !pageItems) return
        const nextItem = pageItems[idxInPage]
        if (!nextItem) {
          message.warning(t('preview.nav.missing'))
          return
        }
        await applyPreviewItem(nextItem)
      } catch {
        if (navSeq === previewNavSeqRef.current) {
          message.error(t('preview.nav.loadFailed'))
        }
      } finally {
        if (navSeq === previewNavSeqRef.current) {
          setPreviewNavBusy(false)
        }
      }
    },
    [ensurePreviewPageItems, applyPreviewItem, t],
  )

  const goPreviewPrev = useCallback(() => {
    void navigatePreviewBy(-1)
  }, [navigatePreviewBy])

  const goPreviewNext = useCallback(() => {
    void navigatePreviewBy(1)
  }, [navigatePreviewBy])

  /**
   * VistoeraTASK-00249 — instant tenant-facing delete: this is `onConfirm`
   * for `VistoeraActionConfirm`, whose popover only closes once
   * `await onConfirm()` resolves (see `VistoeraActionConfirm.
   * handleConfirm`). Deliberately synchronous (no `async`, never awaits
   * the network call) so that await resolves on THIS tick — the popover
   * closes and the row disappears immediately, never waiting on the
   * durable logical-delete request below, let alone the async background
   * physical storage cleanup it enqueues server-side.
   *
   * On a genuine LOGICAL_DELETE_FAILED (the request itself rejects —
   * network/401/403/409/500, never committed server-side) the optimistic
   * removal is rolled back and an error toast reuses the existing
   * `deleteFailedRestored` copy — never leaves the tenant staring at a
   * row that quietly vanished for no visible reason.
   */
  const deleteAsset = (item: LibraryAssetItem) => {
    const id = pickerItemId(item)
    const snapshot = { items, selectedIds, summary, originCounts, total, previewItem }

    setItems((prev) => prev.filter((i) => i.id !== item.id))
    setSelectedIds((prev) => {
      if (!prev.has(item.id)) return prev
      const next = new Set(prev)
      next.delete(item.id)
      return next
    })
    setSummary((prev) => applyDeleteDelta(prev, [item], -1))
    setOriginCounts((prev) => applyOriginDeleteDelta(prev, [item], -1))
    setTotal((prev) => Math.max(0, prev - 1))
    if (previewItem && pickerItemId(previewItem) === id) setPreviewItem(null)

    void deleteImagePickerItem(workspaceId, id)
      .then(() => {
        message.success(t('toast.deleted'))
        invalidateCachedPickerCount(workspaceId)
        // Background reconciliation only — never blocks the UI above.
        // Corrects any summary drift and backfills the current page if
        // this delete emptied part of it.
        void load()
      })
      .catch(() => {
        setItems(snapshot.items)
        setSelectedIds(snapshot.selectedIds)
        setSummary(snapshot.summary)
        setOriginCounts(snapshot.originCounts)
        setTotal(snapshot.total)
        setPreviewItem(snapshot.previewItem)
        message.error(t('toast.deleteFailedRestored'))
      })
  }

  const onEditItemStandard = async (item: LibraryAssetItem) => {
    if (!imageProcessingEnabled) {
      message.warning(t('toast.imageProcessingDisabled'))
      return
    }
    const asset = pickerItemId(item)
    try {
      if (item.chain_id && item.asset_type === 'original') {
        const libraryOnly = Boolean(item.lineage?.library_only || item.metadata?.library_only)
        if (libraryOnly) {
          await startChainProcessing(item.chain_id)
        }
      }
      navigate(`${ITEM_STANDARD_EDITOR_ROUTE(workspaceId)}?asset=${encodeURIComponent(asset)}`, {
        state: { canonicalAssetId: asset, chainId: item.chain_id },
      })
    } catch (e) {
      message.error((e as Error).message || t('toast.cannotOpenItemStandard', { label: itemStandardLabel }))
    }
  }

  const onEditFlat = (item: LibraryAssetItem) => {
    if (!imageProcessingEnabled) {
      message.warning(t('toast.imageProcessingDisabled'))
      return
    }
    const asset = pickerItemId(item)
    if (!item.chain_id) {
      message.warning(t('toast.noChainForFlat', { label: flatOptimizationLabel }))
      navigate(FLAT_IMAGE_EDITOR_ROUTE(workspaceId), {
        state: { canonicalAssetId: asset },
      })
      return
    }
    navigate(FLAT_IMAGE_EDITOR_ROUTE(workspaceId, item.chain_id), {
      state: { canonicalAssetId: asset, chainId: item.chain_id },
    })
  }

  const editMenuItems = (item: LibraryAssetItem) => [
    {
      key: 'item-standard',
      label: itemStandardLabel,
      onClick: () => void onEditItemStandard(item),
    },
    {
      key: 'flat',
      label: flatOptimizationLabel,
      onClick: () => onEditFlat(item),
    },
  ]

  const toggleSelect = (id: string, checked: boolean) => {
    setSelectedIds((prev) => {
      const next = new Set(prev)
      if (checked) next.add(id)
      else next.delete(id)
      return next
    })
  }

  const allPageSelected = items.length > 0 && items.every((i) => selectedIds.has(i.id))
  const somePageSelected = items.some((i) => selectedIds.has(i.id))

  const toggleSelectAllPage = (checked: boolean) => {
    setSelectedIds((prev) => {
      const next = new Set(prev)
      for (const item of items) {
        if (checked) next.add(item.id)
        else next.delete(item.id)
      }
      return next
    })
  }

  const selectedItems = useMemo(
    () => items.filter((i) => selectedIds.has(i.id)),
    [items, selectedIds],
  )
  // VistoeraTASK-00555: delete / visibility batch actions are only offered when
  // EVERY selected image may be managed by the caller (all-or-nothing). Move
  // stays available (any accessible image may be organised).
  const selectionCanManage = useMemo(
    () => !team?.enabled || itemsLackingManage(selectedItems, team).length === 0,
    [selectedItems, team],
  )

  const onSingleDownload = (item: LibraryAssetItem) => {
    const url = item.download_url || item.full_url
    if (!url || singleDownloadingId) return
    setSingleDownloadingId(item.id)
    try {
      triggerImageAssetDownload(url)
    } catch {
      message.error(t('toast.singleDownloadFailed'))
    } finally {
      window.setTimeout(() => setSingleDownloadingId(null), 800)
    }
  }

  /** VistoeraTASK-00727 — browser-native multi-file download via iframe-per-URL (no ZIP).
   * Limit applies only at click time; multi-select itself stays unlimited.
   * Selection is kept. No custom progress UI — Chrome/Edge download tray owns that.
   * TASK-00726: never treat trigger count as browser-blocked.
   * TASK-00727: do not use sync multi-a.click (cross-origin attachment navigations cancel). */
  const MAX_NATIVE_BATCH_DOWNLOAD = 10
  const onBatchDownload = () => {
    if (selectedItems.length === 0 || batchDownloading) return
    const selectedCount = selectedItems.length
    if (selectedCount > MAX_NATIVE_BATCH_DOWNLOAD) {
      message.warning(
        t('toast.batchDownloadLimit', {
          count: selectedCount,
          reduce: selectedCount - MAX_NATIVE_BATCH_DOWNLOAD,
        }),
      )
      return
    }
    const urls = selectedItems
      .map((item) => item.download_url || item.full_url)
      .filter((u): u is string => Boolean(u?.trim()))
    const missingUrlCount = selectedCount - urls.length
    if (urls.length === 0) {
      message.warning(t('toast.noDownloadUrl'))
      return
    }
    // Fire all attachment navigations in the same click gesture (no await /
    // no packing). Unlock immediately — do not wait for disk save.
    setBatchDownloading(true)
    try {
      const { triggered } = triggerImageAssetDownloadsNative(urls)
      if (triggered === 0) {
        message.error(t('toast.batchDownloadFailed'))
        return
      }
      // Only report detectable URL gaps — never invent "browser blocked".
      if (missingUrlCount > 0) {
        message.warning(
          t('toast.batchDownloadPartial', {
            ok: triggered,
            fail: missingUrlCount,
          }),
        )
      }
    } catch {
      message.error(t('toast.batchDownloadFailed'))
    } finally {
      setBatchDownloading(false)
    }
  }

  /**
   * VistoeraTASK-00249 — one Batch Intent request (`batchDeleteImagePickerItems`
   * — a single `POST .../items/batch-delete`, NOT N sequential
   * `deleteImagePickerItem` calls) fired from a SYNCHRONOUS `onOk`.
   * antd's `Modal.confirm` only keeps the OK button in a loading state /
   * defers closing when `onOk` returns a pending Promise (see
   * `Modal.confirm` docs) — this `onOk` returns `undefined` instead, so
   * the confirm modal closes on this exact click, before the network
   * request the returned optimistic UI update below even resolves.
   * Selection/rows/summary all update in the same synchronous batch of
   * state updates as the modal close, so there is never a moment where
   * "已选择 N 项" is shown with the underlying rows already gone (or vice
   * versa).
   */
  const onBatchDelete = () => {
    if (selectedItems.length === 0) return
    // Team mode (VistoeraTASK-00555): a selection containing ANY image the
    // caller may not delete is refused as a whole - nothing is sent, nothing is
    // silently skipped (the server enforces the same all-or-nothing rule).
    const targets = [...selectedItems]
    if (teamEnabled && itemsLackingManage(selectedItems, team).length > 0) {
      message.warning(t('team.toast.batchDeleteDenied'))
      return
    }
    const count = targets.length
    Modal.confirm({
      title: t('deleteConfirm.batchTitle', { count }),
      width: 520,
      centered: true,
      okText: tActions('delete'),
      okType: 'danger',
      cancelText: tActions('cancel'),
      content: (
        <div>
          <p>{t('deleteConfirm.batchIntro')}</p>
          <ul style={{ margin: '8px 0 0', paddingLeft: 20 }}>
            <li>{t('deleteConfirm.batchBullet1')}</li>
            <li>{t('deleteConfirm.batchBullet2')}</li>
          </ul>
        </div>
      ),
      onOk: () => {
        if (batchDeleteInFlightRef.current) return
        batchDeleteInFlightRef.current = true

        const targetKeySet = new Set(targets.map((item) => item.id))
        const targetPickerIds = targets.map((item) => pickerItemId(item))
        const snapshot = { items, selectedIds, summary, originCounts, total }

        setItems((prev) => prev.filter((i) => !targetKeySet.has(i.id)))
        setSelectedIds((prev) => {
          if (prev.size === 0) return prev
          const next = new Set(prev)
          for (const id of targetKeySet) next.delete(id)
          return next
        })
        setSummary((prev) => applyDeleteDelta(prev, targets, -1))
        setOriginCounts((prev) => applyOriginDeleteDelta(prev, targets, -1))
        setTotal((prev) => Math.max(0, prev - targets.length))
        if (previewItem && targetKeySet.has(previewItem.id)) setPreviewItem(null)

        void batchDeleteImagePickerItems(workspaceId, targetPickerIds)
          .then(({ data }) => {
            const ok = data.accepted.length
            const fail = data.rejected.length
            invalidateCachedPickerCount(workspaceId)
            if (fail === 0) message.success(t('toast.batchDeleteSuccess', { count: ok }))
            else if (ok > 0) message.warning(t('toast.batchDeletePartial', { ok, fail }))
            else message.error(t('toast.deleteFailed'))
            // Background reconciliation — authoritative summary + page
            // backfill after a large delete. Never awaited by the UI
            // above; purely corrects drift once the server has settled.
            void load()
          })
          .catch(() => {
            // Whole-request LOGICAL_DELETE_FAILED (network/401/403/500 —
            // never committed server-side): restore exactly what the
            // tenant saw before this click and refetch to reconfirm.
            setItems(snapshot.items)
            setSelectedIds(snapshot.selectedIds)
            setSummary(snapshot.summary)
            setOriginCounts(snapshot.originCounts)
            setTotal(snapshot.total)
            message.error(t('toast.batchDeleteFailedRestored', { count }))
            void load()
          })
          .finally(() => {
            batchDeleteInFlightRef.current = false
          })
      },
    })
  }

  // ---- Team sharing handlers (only reachable in team Workspaces) --------

  const creatorLabel = (item: LibraryAssetItem): string => {
    if (!item.creator) return t('team.creator.unknown')
    return item.creator.is_me ? t('team.creator.me') : item.creator.display_name
  }

  const shareLabel = (item: LibraryAssetItem): string =>
    item.visibility === 'private' ? t('team.share.private') : t('team.share.shared')

  const onChangeView = (next: ImageLibraryView) => {
    if (next === view) return
    // q / type / source / sort are intentionally preserved across views.
    // VistoeraTASK-00580 — entering 我的资产 clears creator filter so a hidden
    // selection cannot keep affecting the query.
    setView(next)
    setCreatorFilter((prev) => creatorFilterAfterViewChange(next, prev))
    setCollectionId(null)
    setPage(1)
    setSelectedIds(new Set())
  }

  const afterTeamChange = () => {
    bumpCollections()
    invalidateCachedPickerCount(workspaceId)
    void load()
  }

  const changeVisibility = async (targets: LibraryAssetItem[], next: ImageVisibility) => {
    if (targets.length === 0) return
    // All-or-nothing (VistoeraTASK-00555): any image the caller may not
    // re-share refuses the whole request; nothing is sent or half-applied.
    if (itemsLackingManage(targets, team).length > 0) {
      message.warning(t('team.toast.batchVisibilityDenied'))
      return
    }
    const manageable = targets
    try {
      const { data } = await setImageVisibility(manageable.map(pickerItemId), next)
      const ok = data.updated.length
      const fail = data.rejected.length
      if (ok > 0) {
        const okSet = new Set(data.updated)
        setItems((prev) =>
          prev.flatMap((i) => {
            if (!okSet.has(pickerItemId(i))) return [i]
            // Left the current view (e.g. unshared while looking at 团队共享).
            if (view === 'shared' && next === 'private') return []
            return [{ ...i, visibility: next }]
          }),
        )
        setSelectedIds(new Set())
      }
      if (fail === 0) {
        message.success(
          t(next === 'private' ? 'team.toast.privateOk' : 'team.toast.sharedOk', { count: ok }),
        )
      } else if (ok > 0) {
        message.warning(t('team.toast.sharePartial', { ok, fail }))
      } else {
        message.error(t('team.toast.noPermission'))
      }
    } catch (e) {
      message.error((e as Error).message || t('team.toast.shareFailed'))
    } finally {
      afterTeamChange()
    }
  }

  const openMove = (targets: LibraryAssetItem[]) => {
    // Moving/archiving is allowed for every image the caller can access
    // (own + teammates' shared) - NOT limited to creator/Owner/Admin.
    const manageable = targets.filter((i) => imageAssetPermissions(i, team).canOrganize)
    if (manageable.length === 0) {
      message.warning(t('team.toast.noPermission'))
      return
    }
    if (manageable.length < targets.length) {
      message.warning(t('team.toast.skippedNoPermission', { count: targets.length - manageable.length }))
    }
    setMoveCollectionId(team?.unsorted_collection_id ?? null)
    setMoveTargets(manageable)
  }

  const submitMove = async () => {
    if (!moveTargets || moveTargets.length === 0) return
    setMoveSaving(true)
    try {
      const target =
        moveCollectionId && moveCollectionId !== team?.unsorted_collection_id ? moveCollectionId : null
      const { data } = await moveImagesToCollection(moveTargets.map(pickerItemId), target)
      const ok = data.updated.length
      const fail = data.rejected.length
      if (fail === 0) message.success(t('team.move.moved', { count: ok }))
      else if (ok > 0) message.warning(t('team.move.partial', { ok, fail }))
      else message.error(t('team.move.failed'))
      setMoveTargets(null)
      setSelectedIds(new Set())
    } catch (e) {
      message.error((e as Error).message || t('team.move.failed'))
    } finally {
      setMoveSaving(false)
      afterTeamChange()
    }
  }

  // Same canonical API + same dialog as the picker (rejecting keeps the dialog
  // open and shows the server's reason inline).
  const submitFolderModal = async (name: string, st: FolderNameModalState) => {
    if (st.mode === 'create') {
      await createImageCollection(name, st.parentId)
      message.success(t('team.collections.created'))
    } else {
      await renameImageCollection(st.folderId, name)
      message.success(t('team.collections.renamed'))
    }
    setFolderModal(null)
    bumpCollections()
  }

  const openCreateFolder = (parentId: string | null) => {
    const parent = parentId ? collections.find((f) => f.id === parentId) : null
    setFolderModal({ mode: 'create', parentId, parentName: parent?.name ?? null })
  }

  const openRenameFolder = (c: { id: string; name: string }) =>
    setFolderModal({ mode: 'rename', folderId: c.id, name: c.name })

  const openFolder = (id: string | null) => {
    setCollectionId(id)
    setPage(1)
    setSelectedIds(new Set())
  }

  const confirmDeleteCollection = (c: ImageCollection) => {
    Modal.confirm({
      title: t('team.collections.deleteTitle', { name: c.name }),
      content: t('team.collections.deleteDescription'),
      okText: tActions('delete'),
      okType: 'danger',
      cancelText: tActions('cancel'),
      centered: true,
      onOk: async () => {
        try {
          await deleteImageCollection(c.id)
          message.success(t('team.collections.deleted'))
          if (collectionId === c.id) setCollectionId(null)
          bumpCollections()
        } catch (e) {
          // e.g. 409 "资料库不为空" - surfaced verbatim, nothing was deleted.
          message.error((e as Error).message)
          throw e
        }
      },
    })
  }

  const confirmDeleteSingle = (item: LibraryAssetItem) => {
    Modal.confirm({
      title: t('deleteConfirm.singleTitle'),
      content: t('deleteConfirm.singleDescription'),
      okText: tActions('delete'),
      okType: 'danger',
      cancelText: tActions('cancel'),
      centered: true,
      onOk: () => {
        deleteAsset(item)
      },
    })
  }

  const teamMenuItems = (item: LibraryAssetItem) => {
    // VistoeraTASK-00555: ownership = created_by_user_id === authenticated user
    // id, role = canonical Workspace membership role. Forbidden entries are
    // HIDDEN (not disabled). No extra per-card request - everything needed is
    // already in the list payload (`team.user_id` / `team.role` / item fields).
    const perms = imageAssetPermissions(item, team)
    const downloadable = Boolean(item.download_url || item.full_url)
    const menu: Array<Record<string, unknown>> = [
      {
        key: 'edit',
        label: t('team.menu.edit'),
        onClick: () => openImageEditor(item),
        'data-testid': 'iac-menu-edit',
      },
      {
        key: 'join-canvas',
        label: t('team.menu.joinCanvas'),
        onClick: () => void onJoinCanvas(item),
        'data-testid': 'iac-menu-join-canvas',
      },
      {
        key: 'download',
        label: t('team.menu.download'),
        disabled: !downloadable,
        onClick: () => onSingleDownload(item),
      },
    ]
    if (perms.canOrganize) {
      menu.push({ key: 'move', label: t('team.menu.move'), onClick: () => openMove([item]) })
    }
    if (perms.canChangeVisibility) {
      menu.push(
        item.visibility === 'private'
          ? {
              key: 'share',
              label: t('team.menu.makeShared'),
              onClick: () => void changeVisibility([item], 'workspace'),
            }
          : {
              key: 'unshare',
              label: t('team.menu.makePrivate'),
              onClick: () => void changeVisibility([item], 'private'),
            },
      )
    }
    menu.push({ key: 'detail', label: t('team.menu.detail'), onClick: () => void openPreview(item) })
    if (perms.canDelete) {
      menu.push(
        { type: 'divider' },
        {
          key: 'delete',
          danger: true,
          label: t('team.menu.delete'),
          onClick: () => confirmDeleteSingle(item),
        },
      )
    }
    return menu
  }

  const personalCardMenuItems = (item: LibraryAssetItem) => {
    const downloadable = Boolean(item.download_url || item.full_url)
    return [
      {
        key: 'edit',
        label: t('team.menu.edit'),
        onClick: () => openImageEditor(item),
        'data-testid': 'iac-menu-edit',
      },
      {
        key: 'join-canvas',
        label: t('team.menu.joinCanvas'),
        onClick: () => void onJoinCanvas(item),
        'data-testid': 'iac-menu-join-canvas',
      },
      {
        key: 'download',
        label: t('team.menu.download'),
        disabled: !downloadable,
        onClick: () => onSingleDownload(item),
      },
      { type: 'divider' as const },
      {
        key: 'delete',
        danger: true,
        label: t('team.menu.delete'),
        onClick: () => confirmDeleteSingle(item),
      },
    ]
  }

  const currentCollection = collectionId
    ? collections.find((c) => c.id === collectionId || (collectionId === 'unsorted' && c.is_system))
    : undefined
  const showCollectionStrip = teamEnabled && view !== 'mine'
  // TASK-00523: drill-down tree - the strip lists only the CURRENT folder's children.
  const currentFolder = currentCollection && !currentCollection.is_system ? currentCollection : null
  const folderCrumbs = collections.filter((f) => !f.is_system)
  const stripCollections = collections.filter((f) =>
    f.is_system ? !currentFolder : (f.parent_id ?? null) === (currentFolder?.id ?? null),
  )
  const teamCanManageCollections = Boolean(team?.can_manage_collections)

  // Exactly four first-level cards, always in this order (VistoeraTASK-00557).
  const typeTabs = useMemo(
    () =>
      ORIGIN_CARD_KEYS.map((key) => ({
        key,
        label: t(`origins.${key}.label`),
        count: originCounts[key],
      })),
    [originCounts, t],
  )

  /** Fine-grained edit method shown next to an 编辑生成 badge (transparent / color / shadow / crop ...). */
  const editDetail = (item: LibraryAssetItem): string | null => {
    if (originOf(item) !== 'edit_generated') return null
    const key = item.origin_detail || 'edit'
    return t(`origins.detail.${key}`, { defaultValue: typeLabel(item) })
  }

  const rowActions = (item: LibraryAssetItem) => (
    <div className="iac-row-actions">
      <Tooltip title={tActions('download')}>
        <Button
          type="text"
          size="small"
          className="iac-icon-btn"
          icon={<DownloadOutlined />}
          disabled={(!item.download_url && !item.full_url) || singleDownloadingId === item.id}
          loading={singleDownloadingId === item.id}
          onClick={() => onSingleDownload(item)}
          aria-label={tActions('download')}
        />
      </Tooltip>
      <Dropdown menu={{ items: editMenuItems(item) }} trigger={['click']}>
        <Button
          type="text"
          size="small"
          className="iac-icon-btn"
          icon={<EditOutlined />}
          aria-label={tActions('edit')}
          title={tActions('edit')}
        />
      </Dropdown>
      {teamEnabled ? (
        <Dropdown menu={{ items: teamMenuItems(item) as never }} trigger={['click']}>
          <Button
            type="text"
            size="small"
            className="iac-icon-btn"
            icon={<MoreOutlined />}
            aria-label={t('team.menu.more')}
            title={t('team.menu.more')}
            data-testid="iac-row-more"
          />
        </Dropdown>
      ) : (
        <VistoeraActionConfirm
          title={t('deleteConfirm.singleTitle')}
          description={t('deleteConfirm.singleDescription')}
          confirmLabel={tActions('delete')}
          tooltip={tActions('delete')}
          onConfirm={() => deleteAsset(item)}
        >
          <Button
            type="text"
            size="small"
            className="iac-icon-btn iac-icon-danger"
            danger
            icon={<DeleteOutlined />}
            aria-label={tActions('delete')}
          />
        </VistoeraActionConfirm>
      )}
    </div>
  )

  if (!workspaceId) return null

  const selectedCount = selectedIds.size

  return (
    <div className="iac-page" data-testid="image-asset-center">
      {editorLayer}
      <div className="iac-main">
        <header className="iac-header">
          <div className="iac-heading">
            <h1 className="iac-title">{t('title')}</h1>
            <p className="iac-subtitle">{t('subtitle')}</p>
          </div>
          <div className="iac-actions">
            <Button
              icon={<CloudUploadOutlined />}
              disabled={!imageProcessingEnabled || !uploadConfig?.browser_upload_enabled}
              onClick={() => setUploadOpen(true)}
              data-testid="iac-upload-btn"
            >
              {t('uploadButton')}
            </Button>
            <Tooltip
              title={
                !primarySite?.id
                  ? t('mobileUploadNoSite')
                  : imageProcessingEnabled
                    ? ''
                    : t('toast.imageProcessingDisabled')
              }
            >
              <Button
                icon={<MobileOutlined />}
                disabled={
                  !imageProcessingEnabled ||
                  !uploadConfig?.browser_upload_enabled ||
                  !primarySite?.id
                }
                onClick={() => setMobileCaptureOpen(true)}
                data-testid="iac-mobile-upload-btn"
              >
                {t('mobileUploadButton')}
              </Button>
            </Tooltip>
            {uploadChip ? (
              <Tooltip title={t('uploadBulk.chipTitle')}>
                <Button
                  type="link"
                  danger={uploadChip.warn}
                  onClick={() => setUploadOpen(true)}
                  data-testid="iac-upload-chip"
                >
                  {uploadChip.label}
                </Button>
              </Tooltip>
            ) : null}
            <Dropdown
              menu={{
                items: [
                  {
                    key: 'batch-dl',
                    label: t('batchActions.download'),
                    disabled: selectedCount === 0 || batchDownloading,
                    onClick: () => void onBatchDownload(),
                  },
                  ...(teamEnabled
                    ? [
                        {
                          key: 'batch-move',
                          label: t('team.menu.move'),
                          disabled: selectedCount === 0,
                          onClick: () => openMove(selectedItems),
                        },
                        ...(selectionCanManage
                          ? [
                              {
                                key: 'batch-share',
                                label: t('team.menu.makeShared'),
                                disabled: selectedCount === 0,
                                onClick: () => void changeVisibility(selectedItems, 'workspace'),
                              },
                              {
                                key: 'batch-unshare',
                                label: t('team.menu.makePrivate'),
                                disabled: selectedCount === 0,
                                onClick: () => void changeVisibility(selectedItems, 'private'),
                              },
                            ]
                          : []),
                      ]
                    : []),
                  ...(selectionCanManage
                    ? [
                        {
                          key: 'batch-del',
                          label: t('batchActions.delete'),
                          danger: true,
                          disabled: selectedCount === 0,
                          onClick: () => onBatchDelete(),
                        },
                      ]
                    : []),
                ],
              }}
            >
              <Button>{t('batchActions.label')}</Button>
            </Dropdown>
            <Button
              icon={<PlusOutlined />}
              onClick={() => navigate(ITEM_STANDARD_EDITOR_ROUTE(workspaceId))}
              data-testid="iac-item-process-btn"
            >
              {itemStandardLabel}
            </Button>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => navigate(FLAT_IMAGE_EDITOR_ROUTE(workspaceId))}
              data-testid="iac-flat-process-btn"
            >
              {flatOptimizationLabel}
            </Button>
          </div>
        </header>

        {teamEnabled && (
          <div className="iac-view-tabs" role="tablist" aria-label={t('team.viewsAria')} data-testid="iac-view-tabs">
            {(['mine', 'shared', 'all'] as const).map((v) => (
              <button
                key={v}
                type="button"
                role="tab"
                aria-selected={view === v}
                className={`iac-view-tab${view === v ? ' is-active' : ''}`}
                onClick={() => onChangeView(v)}
                data-testid={`iac-view-tab-${v}`}
              >
                {t(`team.views.${v}`)}
              </button>
            ))}
          </div>
        )}

        <section className="iac-summary-cards" aria-label={t('summaryAria')}>
          {typeTabs.map((tab) => (
            <button
              key={tab.key}
              type="button"
              className={`iac-summary-card${origin === tab.key ? ' is-active' : ''}`}
              data-testid={`iac-origin-card-${tab.key}`}
              onClick={() => {
                setOrigin(tab.key)
                setPage(1)
              }}
            >
              <div className="iac-summary-label">{tab.label}</div>
              <div className="iac-summary-count">{(tab.count ?? 0).toLocaleString()}</div>
              <div className="iac-summary-hint">{t(`origins.${tab.key}.hint`)}</div>
            </button>
          ))}
        </section>

        {loadError && (
          <Alert
            className="iac-load-error"
            data-testid="iac-load-error"
            type={loadError.kind === 'auth' || loadError.kind === 'forbidden' ? 'error' : 'warning'}
            showIcon
            message={
              loadError.kind === 'other' && loadError.serverMessage
                ? loadError.serverMessage
                : t(`loadError.${loadError.kind}.title`)
            }
            description={
              <>
                {t(`loadError.${loadError.kind}.description`)}
                {items.length > 0 ? ` ${t('loadError.keptData')}` : ''}
              </>
            }
            action={
              loadError.kind === 'auth' ? undefined : (
                <Button size="small" onClick={() => void load()} loading={loading} data-testid="iac-load-error-retry">
                  {t('loadError.retry')}
                </Button>
              )
            }
            style={{ marginBottom: 12 }}
          />
        )}

        <section className="iac-filters">
          <Input
            allowClear
            prefix={<SearchOutlined />}
            placeholder={t('filters.searchPlaceholder')}
            value={q}
            onChange={(e) => {
              setQ(e.target.value)
              setPage(1)
            }}
            className="iac-search"
            data-testid="iac-search"
          />
          <Select
            value={origin}
            onChange={(v) => {
              setOrigin(v)
              setPage(1)
            }}
            options={[
              { value: 'all', label: t('filters.allOrigins') },
              ...ORIGIN_CARD_KEYS.filter((key) => key !== 'all').map((key) => ({
                value: key,
                label: t(`origins.${key}.label`),
              })),
            ]}
            className="iac-filter-select"
            data-testid="iac-origin-filter"
          />
          <Select
            value={sort}
            onChange={(v) => {
              setSort(v)
              setPage(1)
            }}
            options={[
              { value: 'recent', label: t('filters.sortRecent') },
              { value: 'oldest', label: t('filters.sortOldest') },
              { value: 'name', label: t('filters.sortName') },
            ]}
            className="iac-filter-select"
          />
          {teamEnabled && view !== 'mine' && (
            <Select
              value={creatorFilter}
              onChange={(v) => {
                setCreatorFilter(v)
                setPage(1)
              }}
              aria-label={t('team.creator.filterAria')}
              className="iac-filter-select"
              data-testid="iac-creator-filter"
              options={buildCreatorSelectOptions(
                t('team.creator.label'),
                t('team.creator.all'),
                t('team.creator.me'),
                team?.creator_options,
                team?.user_id,
              )}
            />
          )}
          <Tooltip title={tActions('refresh')}>
            <Button icon={<ReloadOutlined />} onClick={() => void load()} aria-label={tActions('refresh')} />
          </Tooltip>
          <div className="iac-view-toggle" role="group" aria-label={t('view.toggleAria')}>
            <Button
              type={viewMode === 'grid' ? 'primary' : 'default'}
              icon={<AppstoreOutlined />}
              onClick={() => persistViewMode('grid')}
              aria-label={t('view.gridAria')}
              data-testid="iac-view-grid"
            />
            <Button
              type={viewMode === 'list' ? 'primary' : 'default'}
              icon={<UnorderedListOutlined />}
              onClick={() => persistViewMode('list')}
              aria-label={t('view.listAria')}
              data-testid="iac-view-list"
            />
          </div>
        </section>

        {showCollectionStrip && (
          <section className="iac-collections" aria-label={t('team.collections.aria')} data-testid="iac-collections">
            {collectionId ? (
              <div className="iac-collection-current" data-testid="iac-collection-current">
                <Button size="small" icon={<ArrowLeftOutlined />} onClick={() => { setCollectionId(currentFolder?.parent_id ?? null); setPage(1); setSelectedIds(new Set()) }}>
                  {t('team.collections.back')}
                </Button>
                {currentFolder ? (
                  <ImageFolderBreadcrumb
                    folders={folderCrumbs}
                    currentFolderId={currentFolder.id}
                    onNavigate={(id) => { setCollectionId(id); setPage(1); setSelectedIds(new Set()) }}
                    testIdPrefix="iac-crumb"
                  />
                ) : (
                  <>
                    <span>{t('team.collections.current')}</span>
                    <span className="iac-collection-current-name">{currentCollection?.name ?? ''}</span>
                  </>
                )}
              </div>
            ) : null}
            <div className="iac-collections-head">
              <h2 className="iac-collections-title">{t('team.collections.title')}</h2>
              <Button
                size="small"
                type="text"
                icon={treeOpen ? <FolderOpenOutlined /> : <FolderOutlined />}
                onClick={toggleTree}
                aria-pressed={treeOpen}
                data-testid="iac-tree-toggle"
              >
                {t('folders.treeTitle')}
              </Button>
              {teamCanManageCollections && (
                <Button
                  size="small"
                  icon={<PlusOutlined />}
                  onClick={() => openCreateFolder(currentFolder?.id ?? null)}
                  data-testid="iac-collection-create"
                >
                  {t('team.collections.create')}
                </Button>
              )}
            </div>
            {treeOpen && (
              <div className="iac-folder-tree-panel" data-testid="iac-folder-tree-panel">
                {/* TASK-00533: the exact tree component the Canvas picker renders. */}
                <ImageFolderTree
                  folders={folderCrumbs}
                  selectedFolderId={currentFolder?.id ?? null}
                  onSelect={(id) => openFolder(id)}
                  canManage={teamCanManageCollections}
                  onCreate={openCreateFolder}
                  onRename={openRenameFolder}
                  onDelete={confirmDeleteCollection}
                  testIdPrefix="iac-tree"
                />
              </div>
            )}
            {collectionsError && (
              <div className="iac-collections-error" data-testid="iac-collections-error" role="status">
                <Button
                  type="link"
                  size="small"
                  icon={<ReloadOutlined />}
                  onClick={() => void loadCollections()}
                  data-testid="iac-collections-retry"
                >
                  {t('loadError.collectionsRetry')}
                </Button>
              </div>
            )}
            <div className="iac-collections-row">
              {stripCollections.map((c) => (
                <div key={c.id} className="iac-collection-wrap" style={{ position: 'relative', flex: '0 0 auto' }}>
                  <button
                    type="button"
                    className={`iac-collection-card${collectionId === c.id ? ' is-active' : ''}`}
                    onClick={() => {
                      setCollectionId(collectionId === c.id ? null : c.id)
                      setPage(1)
                      setSelectedIds(new Set())
                    }}
                    data-testid="iac-collection-card"
                    data-collection-id={c.id}
                  >
                    <span className="iac-collection-name" title={c.name}>{c.name}</span>
                    <span className="iac-collection-count">{t('team.collections.count', { count: c.accessible_asset_count ?? c.count })}</span>
                    <span className="iac-collection-attr">
                      {c.is_system || !c.created_by
                        ? t('team.collections.teamOwned')
                        : t('team.collections.createdBy', { name: c.created_by.display_name })}
                    </span>
                  </button>
                  {teamCanManageCollections && !c.is_system && (
                    <Dropdown
                      trigger={['click']}
                      menu={{
                        items: [
                          {
                            key: 'create-child',
                            label: t('team.collections.createChild'),
                            onClick: () => openCreateFolder(c.id),
                          },
                          {
                            key: 'rename',
                            label: t('team.collections.rename'),
                            onClick: () => openRenameFolder(c),
                          },
                          {
                            key: 'remove',
                            danger: true,
                            label: t('team.collections.remove'),
                            onClick: () => confirmDeleteCollection(c),
                          },
                        ],
                      }}
                    >
                      <button
                        type="button"
                        className="iac-collection-manage"
                        aria-label={t('team.collections.manageAria', { name: c.name })}
                      >
                        <MoreOutlined />
                      </button>
                    </Dropdown>
                  )}
                </div>
              ))}
            </div>
          </section>
        )}

        {selectedCount > 0 && (
          <div className="iac-batch-bar" data-testid="iac-batch-bar">
            <span>{t('selection.count', { count: selectedCount })}</span>
            <Space>
              <Button
                size="small"
                icon={<DownloadOutlined />}
                loading={batchDownloading}
                disabled={selectedItems.length === 0 || batchDownloading}
                onClick={() => void onBatchDownload()}
              >
                {t('batchActions.download')}
              </Button>
              {teamEnabled && (
                <>
                  <Button size="small" onClick={() => openMove(selectedItems)}>
                    {t('team.menu.move')}
                  </Button>
                  {selectionCanManage ? (
                    <>
                      <Button size="small" onClick={() => void changeVisibility(selectedItems, 'workspace')}>
                        {t('team.menu.makeShared')}
                      </Button>
                      <Button size="small" onClick={() => void changeVisibility(selectedItems, 'private')}>
                        {t('team.menu.makePrivate')}
                      </Button>
                    </>
                  ) : (
                    <span data-testid="iac-batch-no-manage-hint" style={{ fontSize: 12, opacity: 0.65 }}>
                      {t('team.toast.batchManageHint')}
                    </span>
                  )}
                </>
              )}
              {selectionCanManage && (
                <Button size="small" danger icon={<DeleteOutlined />} onClick={() => onBatchDelete()}>
                  {t('batchActions.delete')}
                </Button>
              )}
              <Button size="small" type="link" onClick={() => setSelectedIds(new Set())}>
                {t('selection.clear')}
              </Button>
            </Space>
          </div>
        )}

        <Spin spinning={loading}>
          {!loading && items.length === 0 && loadError ? null : !loading && items.length === 0 ? (
            <Empty
              description={
                teamEnabled
                  ? collectionId
                    ? t('team.empty.collection')
                    : t(`team.empty.${view}`)
                  : t('empty.noAssets')
              }
              style={{ padding: '48px 0' }}
            />
          ) : viewMode === 'grid' ? (
            <div className="iac-grid" data-testid="iac-grid">
              {items.map((item, index) => {
                const selected = selectedIds.has(item.id)
                const selIndex = selected ? [...selectedIds].indexOf(item.id) + 1 : 0
                return (
                  <article
                    key={item.id}
                    className={`iac-card${selected ? ' is-selected' : ''}`}
                    data-asset-id={item.id}
                    data-asset-type={item.asset_type}
                  >
                    <div className="iac-card-media">
                      <label className="iac-card-check">
                        <Checkbox
                          checked={selected}
                          onChange={(e) => toggleSelect(item.id, e.target.checked)}
                        />
                        {selected && selIndex > 0 ? (
                          <span className="iac-sel-index">{selIndex}</span>
                        ) : null}
                      </label>
                      {teamEnabled ? (
                        <Dropdown menu={{ items: teamMenuItems(item) as never }} trigger={['click']}>
                          <button
                            type="button"
                            className="iac-card-more"
                            aria-label={t('team.menu.more')}
                            data-testid="iac-card-more"
                          >
                            <MoreOutlined />
                          </button>
                        </Dropdown>
                      ) : (
                        <Dropdown menu={{ items: personalCardMenuItems(item) as never }} trigger={['click']}>
                          <button
                            type="button"
                            className="iac-card-more"
                            aria-label={t('team.menu.more')}
                            data-testid="iac-card-more"
                          >
                            <MoreOutlined />
                          </button>
                        </Dropdown>
                      )}
                      <button
                        type="button"
                        className={`iac-card-preview${item.asset_type === 'transparent' ? ' is-transparent' : ''}`}
                        onClick={() => void openPreview(item)}
                      >
                        {item.preview_url ? (
                          <img src={item.preview_url} alt={item.filename} loading="lazy" />
                        ) : (
                          <span className="iac-card-empty">{t('empty.noPreview')}</span>
                        )}
                      </button>
                    </div>
                    <div className="iac-card-meta">
                      <div className="iac-card-name" title={item.filename}>
                        {item.filename}
                      </div>
                      <div className="iac-card-tags">
                        <span className={originBadgeClass(item)} data-origin={originOf(item)}>
                          {t(`origins.${originOf(item)}.label`)}
                        </span>
                        {editDetail(item) && <span className="iac-source-chip">{editDetail(item)}</span>}
                      </div>
                      <div className="iac-card-sub">
                        <span>{formatDims(item.width, item.height)}</span>
                        <span>{formatBytes(item.size_bytes)}</span>
                      </div>
                      <div className="iac-card-footer">
                        <span className="iac-card-time">{formatAbsoluteTime(item.created_at)}</span>
                        <span className="iac-card-index-hint" aria-hidden>
                          {index + 1}
                        </span>
                      </div>
                      {teamEnabled && (
                        <div className="iac-card-team" data-testid="iac-card-team">
                          <span className="iac-creator" title={creatorLabel(item)}>
                            <span
                              className="iac-avatar"
                              aria-hidden
                              style={{ background: avatarColor(item.creator?.user_id) }}
                            >
                              {creatorInitial(item.creator?.display_name)}
                            </span>
                            <span className="iac-creator-name">{creatorLabel(item)}</span>
                          </span>
                          <span
                            className={`iac-share-badge${item.visibility === 'private' ? '' : ' is-workspace'}`}
                            data-testid="iac-share-badge"
                          >
                            {shareLabel(item)}
                          </span>
                        </div>
                      )}
                    </div>
                  </article>
                )
              })}
            </div>
          ) : (
            <div className="iac-table-scroll" data-testid="iac-list">
              <table className="iac-table">
                <thead>
                  <tr>
                    <th className="iac-th-check">
                      <Checkbox
                        checked={allPageSelected}
                        indeterminate={!allPageSelected && somePageSelected}
                        onChange={(e) => toggleSelectAllPage(e.target.checked)}
                        aria-label={t('table.selectAllPage')}
                      />
                    </th>
                    <th className="iac-th-thumb">{t('table.thumbnail')}</th>
                    <th className="iac-th-name">{t('table.filename')}</th>
                    <th>{t('table.type')}</th>
                    <th>{t('table.detail')}</th>
                    <th>{t('table.dimensionsAndSize')}</th>
                    <th>{t('table.createdAt')}</th>
                    {teamEnabled && <th>{t('team.creator.label')}</th>}
                    {teamEnabled && <th>{t('team.share.column')}</th>}
                    <th className="iac-th-ops">{t('table.operations')}</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((item) => (
                    <tr
                      key={item.id}
                      className={selectedIds.has(item.id) ? 'is-selected' : undefined}
                      data-asset-id={item.id}
                    >
                      <td className="iac-td-check">
                        <Checkbox
                          checked={selectedIds.has(item.id)}
                          onChange={(e) => toggleSelect(item.id, e.target.checked)}
                        />
                      </td>
                      <td className="iac-td-thumb">
                        <button
                          type="button"
                          className={`iac-list-thumb${item.asset_type === 'transparent' ? ' is-transparent' : ''}`}
                          onClick={() => void openPreview(item)}
                          aria-label={t('table.previewAria', { filename: item.filename })}
                        >
                          {item.preview_url ? <img src={item.preview_url} alt="" loading="lazy" /> : null}
                        </button>
                      </td>
                      <td className="iac-td-name">
                        <div className="iac-list-name" title={item.filename}>
                          {item.filename}
                        </div>
                        <div className="iac-list-dims">{formatDims(item.width, item.height)}</div>
                      </td>
                      <td>
                        <span className={originBadgeClass(item)} data-origin={originOf(item)}>
                          {t(`origins.${originOf(item)}.label`)}
                        </span>
                      </td>
                      <td>{editDetail(item) ?? '—'}</td>
                      <td className="iac-td-size">
                        <div>{formatDims(item.width, item.height)}</div>
                        <div className="iac-muted">{formatBytes(item.size_bytes)}</div>
                      </td>
                      <td className="iac-td-time">{formatAbsoluteTime(item.created_at)}</td>
                      {teamEnabled && (
                        <td>
                          <span className="iac-creator">
                            <span
                              className="iac-avatar"
                              aria-hidden
                              style={{ background: avatarColor(item.creator?.user_id) }}
                            >
                              {creatorInitial(item.creator?.display_name)}
                            </span>
                            <span className="iac-creator-name">{creatorLabel(item)}</span>
                          </span>
                        </td>
                      )}
                      {teamEnabled && (
                        <td>
                          <span
                            className={`iac-share-badge${item.visibility === 'private' ? '' : ' is-workspace'}`}
                          >
                            {shareLabel(item)}
                          </span>
                        </td>
                      )}
                      <td className="iac-td-ops">{rowActions(item)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Spin>

        <footer className="iac-pagination">
          <span className="iac-total">{t('pagination.total', { count: total })}</span>
          <Pagination
            current={page}
            pageSize={pageSize}
            total={total}
            showSizeChanger
            pageSizeOptions={PAGE_SIZE_OPTIONS.map(String)}
            onChange={(p, ps) => {
              setPage(p)
              setPageSize(ps)
            }}
          />
        </footer>
      </div>

      <aside className="iac-aside" aria-label={t('aside.panelAria')}>
        <div className="iac-aside-block">
          <h2>{t('aside.filters')}</h2>
          <ul className="iac-type-help">
            {(['upload', 'ai_generated', 'edit_generated'] as const).map((key) => (
              <li key={key}>
                <span className={`iac-badge iac-origin-${key}`}>{t(`origins.${key}.label`)}</span>
                <span>{t(`origins.${key}.hint`)}</span>
              </li>
            ))}
          </ul>
          <p className="iac-aside-note" style={{ marginTop: 12 }}>
            {t('aside.sourceTypeNote')}
          </p>
        </div>
        <div className="iac-aside-block">
          <h2>{t('aside.quickActions')}</h2>
          <div className="iac-quick-actions">
            <Button block icon={<CloudUploadOutlined />} onClick={() => setUploadOpen(true)}>
              {t('uploadButton')}
            </Button>
            <Button
              block
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => navigate(ITEM_STANDARD_EDITOR_ROUTE(workspaceId))}
              data-testid="iac-aside-item-process"
            >
              {itemStandardLabel}
            </Button>
            <Button
              block
              icon={<PlusOutlined />}
              onClick={() => navigate(FLAT_IMAGE_EDITOR_ROUTE(workspaceId))}
              data-testid="iac-aside-flat-process"
            >
              {flatOptimizationLabel}
            </Button>
            <Button block type="link" onClick={() => navigate(ITEM_STANDARD_EDITOR_ROUTE(workspaceId))}>
              {t('quickActions.viewTasks')}
            </Button>
          </div>
        </div>
        <div className="iac-aside-block">
          <h2>{t('aside.usageGuide')}</h2>
          <p className="iac-aside-note">
            {t('aside.usageNote', asideNavLabels)}
          </p>
        </div>
      </aside>

      {/* VistoeraTASK-00575 / 00593 — CanvasHiResImageViewer + Edit → existing editor. */}
      <ImageAssetHiResViewer
        open={!!previewItem}
        onClose={closePreview}
        loading={previewLoading}
        filename={previewItem?.filename || ''}
        displaySrc={
          previewItem
            ? previewItem.display_url || previewItem.preview_url || null
            : null
        }
        masterSrc={previewItem?.full_url || null}
        canDownload={Boolean(previewItem?.download_url || previewItem?.full_url)}
        onDownload={() => {
          if (previewItem) onSingleDownload(previewItem)
        }}
        canEdit={
          !!previewItem &&
          (!teamEnabled || !team || imageAssetPermissions(previewItem, team).canOrganize)
        }
        onEdit={() => {
          if (!previewItem) return
          // Snapshot before closing so the editor receives the viewed asset.
          const item = previewItem
          closePreview()
          openImageEditor(item)
        }}
        isTransparent={previewItem?.asset_type === 'transparent'}
        hasPrev={Boolean(previewNavSession && canGoPrev(previewNavSession.absoluteIndex))}
        hasNext={Boolean(
          previewNavSession && canGoNext(previewNavSession.absoluteIndex, previewNavSession.total),
        )}
        onPrev={goPreviewPrev}
        onNext={goPreviewNext}
        navBusy={previewNavBusy}
        meta={
          previewItem ? (
            <>
              <div>
                <dt>{t('preview.meta.filename')}</dt>
                <dd title={previewItem.filename}>{previewItem.filename}</dd>
              </div>
              <div>
                <dt>{t('preview.meta.origin')}</dt>
                <dd>{t(`origins.${originOf(previewItem)}.label`)}</dd>
              </div>
              {editDetail(previewItem) && (
                <div>
                  <dt>{t('preview.meta.detail')}</dt>
                  <dd>{editDetail(previewItem)}</dd>
                </div>
              )}
              <div>
                <dt>{t('preview.meta.dimensions')}</dt>
                <dd>{formatDims(previewItem.width, previewItem.height)}</dd>
              </div>
              <div>
                <dt>{t('preview.meta.fileSize')}</dt>
                <dd>{formatBytes(previewItem.size_bytes)}</dd>
              </div>
              <div>
                <dt>{t('preview.meta.createdAt')}</dt>
                <dd>{formatAbsoluteTime(previewItem.created_at)}</dd>
              </div>
              {teamEnabled && (
                <>
                  <div>
                    <dt>{t('team.creator.label')}</dt>
                    <dd>{creatorLabel(previewItem)}</dd>
                  </div>
                  <div>
                    <dt>{t('team.share.column')}</dt>
                    <dd>{shareLabel(previewItem)}</dd>
                  </div>
                </>
              )}
            </>
          ) : null
        }
      />

      {scopeReady && (
        <ImageAssetUploadModal
          open={uploadOpen}
          workspaceId={workspaceId}
          config={uploadConfig}
          onClose={() => setUploadOpen(false)}
          autoProcess={false}
          assetSource="browser_upload"
          title={t('uploadModalTitle')}
          placement={
            teamEnabled
              ? {
                  // TASK-00523: show the full path so nested folders stay distinguishable.
                  collections: collections.map((c) => ({
                    id: c.id,
                    name: c.is_system ? c.name : folderPath(folderCrumbs, c.id).map((p) => p.name).join(' / ') || c.name,
                  })),
                  unsortedId: team?.unsorted_collection_id ?? null,
                  defaultCollectionId: collectionId,
                  canCreateCollection: teamCanManageCollections,
                  onCreateCollection: async (name: string) => {
                    const { data } = await createImageCollection(name)
                    setCollections((prev) => [...prev, data])
                    bumpCollections()
                    return { id: data.id, name: data.name }
                  },
                }
              : undefined
          }
        />
      )}

      {/* VistoeraTASK-00600 — reuse shared MobileCaptureQrModal; target is
          workspace_asset_library (NOT item_image_processing). Lifecycle and
          session state are local to this page so ImagesPage cannot share it. */}
      <MobileCaptureQrModal
        open={mobileCaptureOpen}
        workspaceId={workspaceId}
        siteId={primarySite?.id}
        targetFlow="workspace_asset_library"
        uploadHintKey="mobileCapture.uploadHintAssetLibrary"
        onClose={() => {
          setMobileCaptureOpen(false)
          invalidateCachedPickerCount(workspaceId)
          void load()
          if (teamEnabled) bumpCollections()
        }}
        onSessionActiveChange={setMobileCapturePolling}
      />

      {teamEnabled && (
        <>
          <FolderNameModal
            state={folderModal}
            onCancel={() => setFolderModal(null)}
            onSubmit={submitFolderModal}
            testIdPrefix="iac-collection"
          />
          <Modal
            open={!!moveTargets}
            title={t('team.move.title')}
            onCancel={() => setMoveTargets(null)}
            onOk={() => void submitMove()}
            confirmLoading={moveSaving}
            okText={t('team.move.confirm')}
            cancelText={tActions('cancel')}
            destroyOnClose
            centered
          >
            <p>{t('team.move.description', { count: moveTargets?.length ?? 0 })}</p>
            <Select
              style={{ width: '100%' }}
              value={moveCollectionId ?? undefined}
              placeholder={t('team.move.select')}
              onChange={(v) => setMoveCollectionId(v)}
              options={collections.map((c) => ({
                value: c.id,
                label: c.is_system ? c.name : folderPath(folderCrumbs, c.id).map((p) => p.name).join(' / ') || c.name,
              }))}
              data-testid="iac-move-select"
            />
          </Modal>
        </>
      )}
    </div>
  )
}
